import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Column } from '@miq/catalog';
import { SelectionTableOptions } from '@shared/components/selection-table/selection-table.component';

export interface SelectionTableDialogData {
  columns: Pick<Column, 'field' | 'header'>[];
  elements: Record<string, any>[];
  options: SelectionTableOptions & { confirmText?: string; cancelText?: string; title?: string };
}

@Component({
  selector: 'miq-selection-table-dialog',
  template: ` <!-- dialog -->
    <div class="dialog">
      <mat-icon mat-dialog-close (click)="close()" class="dialog__close" aria-hidden="false" aria-label="Close icon"
        >close
      </mat-icon>

      <!-- title -->
      <div mat-dialog-title>
        <h1>{{ title ?? 'shared.select' | xTranslate }}</h1>
      </div>

      <!-- content -->
      <div mat-dialog-content class="confirm-container flex-grid">
        <miq-selection-table [elements]="elements" [columns]="columns" [options]="options" [(selected)]="selected">
        </miq-selection-table>

        <!-- actions -->
        <div mat-dialog-actions class="mat-dialog-actions">
          <button *ngIf="cancelText" mat-button color="primary" (click)="close()">
            {{ cancelText | xTranslate }}
          </button>

          <button
            *ngIf="confirmText"
            mat-flat-button
            color="primary"
            [disabled]="!selectionValid"
            (click)="close(selected)"
          >
            {{ confirmText | xTranslate }}
          </button>
        </div>
      </div>
    </div>`,
  styleUrls: ['./selection-table-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectionTableDialogComponent {
  title = 'shared.select';
  cancelText: string = null;
  confirmText: string = null;
  options: SelectionTableOptions = null;
  columns: Pick<Column, 'field' | 'header'>[] = [];
  elements: Record<string, any>[] = [];

  selected: never | never[] = null;

  constructor(
    public dialogRef: MatDialogRef<SelectionTableDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SelectionTableDialogData
  ) {
    this.#initDialog(data);
  }

  #initDialog({ options, columns, elements }: SelectionTableDialogData) {
    const { title, confirmText, cancelText, ...tableOptions } = options;

    this.title = title ?? this.title;

    this.confirmText = confirmText;
    this.cancelText = cancelText;

    this.columns = columns;
    this.elements = elements;
    this.options = tableOptions;
  }

  get selectionValid() {
    if (this.options.selectMode === 'single') {
      return !!this.selected;
    }
    return this.selected?.length > 0;
  }

  close(selection?: never | never[]) {
    this.dialogRef.close(selection);
  }
}
